<template lang="pug">
.chart(ref="chart", :class="chartClasses()", :style="chartStyles")
  .chart-wrapper(:class="chartWrapperClass", ref="chartWrapper", :style="chartWrapperStyles")
    .chat-visible-wrapper(:class="chatVisibleClasses()")
      .chat-wrapper
        chat.chart-chat(
          v-if="id",
          :permitted="true",
          :paths="dashboardChatPaths",
          :hideWelcome="false",
          headerAdditionalClass="dashboard-chat-header",
          footerAdditionalClass="dashboard-chat-footer",
          :hideFooterPlaceholder="true",
          :focusFooterOnLoaded="false",
          :chatId="id",
          :fromDashboard="true",
          @create-chart="createChart"
        )
          template(v-slot:header) {{ dashboardAiLocales.chart_header }}

      button.chart-chat-toggle-button(@click="toggleShowChat")
        q-icon(name="chevron_left")

    q-skeleton(v-if="chartLoading", animation="blink", :dark="theme === 'dark'", :style="{ width: '100%' }")
    .chart-component-wrapper(v-else)
      bar-chart.chart-component(
        v-if="currentChart?.type === 'bar'",
        :chartData="currentChart",
        :barColumnWidthCoef="getBarColumnWidthCoef()",
        :legendMarginRight="25",
        :height="getChartHeight()",
        :maxSeries="getBarMaxSeries()"
      )
      candle-chart.chart-component(
        v-else-if="currentChart?.type === 'candle'",
        :chartData="currentChart",
        :height="getChartHeight()",
        :maxSeries="getCandleMaxSeries()",
        :buttonsMarginRight="25"
      )
      pie-chart.chart-component(
        v-else-if="currentChart?.type === 'pie'",
        :chartData="currentChart",
        :height="getPieHeight()",
        :wrapLegend="getPieWrapLegend()"
      )
      .chart-plug(v-else)
        inline-svg.chart-plug-image(:src="require('../../assets/images/dasboard_chart_plug.svg')")
        b.chart-plug-label {{ dashboardAiLocales.plug_label }}
        p.chart-plug-sublabel {{ dashboardAiLocales.plug_sublabel }}

    inline-svg.chart-wrapper-remove(
      v-if="!chartLoading",
      :src="require('../../assets/icons/ai/trash.svg')",
      fill="transparent",
      @click="deleteChat()"
    )
    .chart-wrapper-drag-zone-X(@mousedown="dragStartX", :style="{ borderRight: dragZoneBorderRight }")
    .chart-wrapper-drag-zone-Y(@mousedown="dragStartY")
</template>

<script setup>
import { ref, computed, onMounted, onBeforeMount } from "vue";
import chat from "../ai/components/chat";
import backend from "@/api";
import { handleError } from "../../services/handleErrors";
import barChart from "./charts/bar/barChart";
import candleChart from "./charts/candle/candleChart";
import pieChart from "./charts/pie/pieChart";
import { theme } from "@/services/useTheme";
import { dashboardAiLocales } from "@/services/useLocales";

const emit = defineEmits([
  /**
   * Нужен для удаления чата. Принимает id. Пример параметров: 1
   */
  "delete-chat",
]);

const props = defineProps({
  /* Объект, содержащий дату чарта */
  chartData: { type: Object, default: () => {} },
  /* id текущей основной орагнизации выбранной пользователем для дашборда в фильтрах */
  facilityId: { type: Number, default: () => -1 },
  /* Максимальная длина родительского элемента. Нужна для вычислений в растягивании графиков */
  maxWidth: { type: Number, default: () => 1 },
});

const dashboardChatPaths = { main: "api/v3/ai/chats/dashboard", name: "" }; // Нужнен для компонента чата
// Дополнительные параметры к запросам внутри компонента чата
const dashboardChatParams = {
  chat: {
    facility_id: props.facilityId,
    chat_type: "dashboard",
  },
};

const componentFullWidth = "100%";
// 6px это половина от 12px gap в flex родителя. Чтобы умещалось 2 элемента на одной оси flex
const componentHalfWidth = "calc(50% - 6px)";
// 8px это треть от 2-х gap в flex родителя. Чтобы умещалось 3 элемента между которыми 2 gap по 12px
const componentThirdWidth = "calc(33.3333% - 8px)";
// Минимальная высота чарта
const componentMinHeight = 382;
// Максимальная высота чарта
const componentMaxHeight = 653;

// ref обертки компонента. Нужен для вычислений при растягивании графиков
const chartWrapper = ref(null);
// Текущая ширина самого графика
const chartWidth = ref("100%");
// Текущая ширина обертки графика
const chartWrapperWidth = ref("100%");
// inline стиль для драг зоны. Нужна для подсвета "магнита"
const dragZoneBorderRight = ref("none");

const chartHeight = ref(`${componentMinHeight}px`); // Текущая высота чарта
const chartWrapperHeight = ref("100%"); // Текущая высота обертки чарта

const id = ref(props.chartData.id); // id текущего чата
const currentChart = ref({}); // Данные по текущему чарту. Приходят от агента
const showChat = ref(true); // Флаг отображения чата
// Нужно для избежания анимации открытия чата при инициализации компонента. Она нужна только при раскрытии скрытого чата */
const canAnimateOpenChat = ref(false);
const chartLoading = ref(true); // Флаг загрузки чарта
const chartDraggingX = ref(false); // Флаг растягивания чата по оси X
const closestMagnet = ref(null); // Ближайший магнит при перетаскивании
const chartDraggingY = ref(false); // Флаг растягивания чата по оси Y

// Класс обертки чарта. Нужен для анимации только созданных чатов
const chartWrapperClass = computed(() => (id.value ? "" : "just-created"));
const chatHiddenClass = computed(() => (showChat.value ? "" : "hidded")); // Класс для скрытия чата
const chartMaxWidth = computed(() => props.maxWidth); // Макс. ширина чарта
const chartHalfWidth = computed(() => chartMaxWidth.value / 2); // Половинная длина чарта
const chartThirdWidth = computed(() => chartMaxWidth.value / 3); // Треть длины чарта
// Иконка удаления чата в зависимости от текущей темы
const removeIconPath = computed(() =>
  theme.value === "dark" ? "../../assets/icons/ai/trash-dark.svg" : "../../assets/icons/ai/trash.svg",
);
// Стили чарта. Меняются после растягивания
const chartStyles = computed(() => {
  return {
    width: chartWidth.value,
    height: chartHeight.value,
  };
});
// Стили обертки чарта. Меняются при растягивании
const chartWrapperStyles = computed(() => {
  return {
    width: chartWrapperWidth.value,
    height: chartWrapperHeight.value,
  };
});

/* Функция возвращает базовую ширину бара графика типа "bar" в зависимости от длины. Нужно для одинаковой ширины колонок при всех размерах графика */
const getBarColumnWidthCoef = () => {
  if (chartWidth.value === componentHalfWidth) {
    return 2;
  } else if (chartWidth.value === componentThirdWidth) {
    return 3;
  } else {
    return 1;
  }
};

// Функция возвращает высоту самого чарта в зависимости от растянутой высоты
const getChartHeight = () => {
  /* Отступы в чарте. При высоте компонента 392px мы должны иметь высоту самого компонента apexchart 300px для корректного отображения */
  const barChartMargins = 92;
  const numberHeight = Number(chartHeight.value.replace("px", ""));
  return numberHeight - barChartMargins;
};

// Функция возвращает максимально видимое количество баров в графике типа "bar"
const getBarMaxSeries = () => {
  if (chartWidth.value === componentHalfWidth) {
    // 12 баров при половинном размере
    return 12;
  } else if (chartWidth.value === componentThirdWidth) {
    // 8 баров при трети
    return 8;
  } else if (showChat.value) {
    // 20 баров для полной ширины чарта если чат открыт (он занимает примерно 280px)
    return 20;
  } else {
    // 24 бара для полной ширины чарта, если чат скрыт
    return 24;
  }
};

// Функция возвращает максимально видимое количество баров в графике типа "bar"
const getCandleMaxSeries = () => {
  if (chartWidth.value === componentHalfWidth) {
    // 6 баров + бар для суммы для половины ширины чарта
    return 7;
  } else if (chartWidth.value === componentThirdWidth) {
    // 3 бара + бар для суммы для трети ширины чарта
    return 4;
  } else if (showChat.value) {
    // 9 баров + бар для суммы для полной ширины чарта если чат открыт (он занимает примерно 280px)
    return 10;
  } else {
    // 12 баров + бар для суммы для полной ширины чарта, если чат скрыт
    return 13;
  }
};

// Функция возвращает высоту графика типа "pie". А так же и ширину, т.к. график круглый
const getPieHeight = () => {
  if (getPieWrapLegend()) {
    /* 200 это высота самого чарта когда легенда переносится под него. Ни больше, ни меньше корректно отображаться не будет */
    return 200;
  } else if (chartWidth.value === componentHalfWidth) {
    /* Вычитаем 100px из высоты и ширины чарта за каждый элемент легенды, чтобы она отображалась корректно */
    const additionalMinusWidth = (currentChart.value?.legend?.text.length - 1) * 100;
    return getChartHeight() - additionalMinusWidth;
  } else {
    return getChartHeight();
  }
};

// Функция возвращает флаг надобности переноса легенды под график в графике типа "pie"
const getPieWrapLegend = () => {
  if (chartWidth.value === componentHalfWidth) {
    return false;
  } else if (chartWidth.value === componentThirdWidth) {
    return true;
  } else {
    return false;
  }
};

/* Функция возвращает классы для самого чата. Нужно для скрытия и открытия, а так же для абсолютного позиционирования при размере половина и треть */
const chatVisibleClasses = () => {
  const classes = [chatHiddenClass.value];

  if (canAnimateOpenChat.value) classes.push("animate-open");
  /* Если размер чата занимает треть, задаем самому чату дополнительный класс, который вешает position absolute */
  if (chartWidth.value === componentThirdWidth || chartWidth.value === componentHalfWidth) classes.push("absolute");

  return classes.join(" ");
};

// Функция возвращает классы чарта в зависисоит от того в какую ось растягивают график
const chartClasses = () => {
  const classes = [];

  if (chartDraggingX.value) classes.push("draggingX");
  if (chartDraggingY.value) classes.push("draggingY");

  return classes.join(" ");
};

// Функция создает новый чат и задает его новый id
const createNewChat = async () => {
  try {
    const { data } = await backend.create("/api/v3/ai/chats/dashboard", "", dashboardChatParams);
    id.value = data.id;
  } catch (err) {
    await handleError();
  }
};

// Функция удаления чата при нажатии на иконку
const deleteChat = () => {
  emit("delete-chat", id.value);
};

// Функция создания фронтом уже существующего на беке чата в зависимости от последнего ответа от агента
const createChart = async resp => {
  chartLoading.value = true;

  // Если агент ничего не ответил чарт сбросится и установится заглушка
  if (resp === "empty" || !Object.keys(resp?.options).length) {
    currentChart.value = {};
    currentChart.value["type"] = "empty";
    chartLoading.value = false;
    return;
  }

  // Получаем чарт в зависимости от параметров от агента
  try {
    const params = {
      options: resp?.options,
      facility_id: props.facilityId,
    };

    const { data } = await backend.index("/api/v3/ai/dashboard/chart", { params });

    currentChart.value = data;

    // Если получаем флаг на то что ось X является датой, то переводим ее в формат "DD.MM.YYYY"
    if (data.x_is_date) {
      const localDateOptions = {
        year: "numeric",
        month: "numeric",
        day: "numeric",
      };

      data.categories.forEach((item, i) => {
        const date = new Date(item);
        currentChart.value.categories[i] = date.toLocaleDateString("ru-RU", localDateOptions);
      });
    }
  } catch (err) {
    await handleError(err);
  } finally {
    chartLoading.value = false;
  }
};

// Функция скрытия и открытия чата
const toggleShowChat = () => {
  /* Назначаем возможность анимации открытия в зависимости от значения showChat до его переключения */
  if (!showChat.value) {
    canAnimateOpenChat.value = true;
  } else {
    canAnimateOpenChat.value = false;
  }

  showChat.value = !showChat.value;

  // Проверяем есть ли график
  if (currentChart.value?.type) {
    chartLoading.value = true;
  }

  // 200 это время анимации скрытия или открытия чата в мс
  setTimeout(() => {
    chartLoading.value = false;
  }, 200);
};

// Функция начала растягивания по оси X
const dragStartX = () => {
  chartDraggingX.value = true; // Задаем флаг растягивания true
  chartLoading.value = true; // Задаем флаг загрузки чарта true
  chartWrapperWidth.value = chartWidth.value; // Запоминаем текущий размер чарта и задаем его обертке
  // Вешаем слушатель на mousemove и mouseup для document
  document.addEventListener("mousemove", draggingX);
  document.addEventListener("mouseup", dragEndX);
  // Назначаем нужный нам курсор
  document.body.style.cursor = "ew-resize";
  document.body.style.userSelect = "none";
};

// Функция "примагничивания" при растягивании по оси X
const magnetChart = magnetWidth => {
  // Задаем размер ширины чарта на текущий "магнит"
  chartWrapperWidth.value = magnetWidth;
  // Создаем зеленую полоску по середине или трети для наглядности куда был применен "магнит"
  dragZoneBorderRight.value = "1px solid #14C783";
};

// Функция перетаскивания по оси X
const draggingX = e => {
  if (chartDraggingX.value) {
    // Получаем текущую позицию оси X как если бы у нашего элемента не было отступов слева
    const chartWrapperMarginLeftFromWindow = chartWrapper.value?.getBoundingClientRect().left;
    const xWithoutMarginLeft = e.x - chartWrapperMarginLeftFromWindow;

    // Отклонения от точной ширины для "магнита" к нужной зоне +- 10px
    const magnetWidthDeviations = 10;

    // Вычисление длины графика в процентах в зависимости от текущего положения x и максимальной длины
    const chartWidthAsPercent = (xWithoutMarginLeft / chartMaxWidth.value) * 100;

    // Проверяем близко ли к максимальной длине чарта пользователь и примагничиваем к нему если да
    if (xWithoutMarginLeft > chartMaxWidth.value - magnetWidthDeviations) {
      chartWrapperWidth.value = componentFullWidth;
      // Проверяем дошел ли юзер до середины +- 10px для "магнита"
    } else if (
      xWithoutMarginLeft < chartHalfWidth.value + magnetWidthDeviations &&
      xWithoutMarginLeft > chartHalfWidth.value - magnetWidthDeviations
    ) {
      magnetChart(componentHalfWidth);
      // Проверяем дошел ли юзер до трети +- 10px для "магнита"
    } else if (xWithoutMarginLeft < chartThirdWidth.value + magnetWidthDeviations) {
      magnetChart(componentThirdWidth);
    } else {
      // Если "магнитов" нет, и чарт не выходит за пределы максимальной и минимальной ширины то двигаем размер за курсором
      chartWrapperWidth.value = `${chartWidthAsPercent}%`;
      dragZoneBorderRight.value = "none";
    }

    // Примагничиваем чарты к ближайшему доступному размеру если пользователь бросил перетаскивание
    if (chartWidthAsPercent > 75) {
      closestMagnet.value = componentFullWidth;
    } else if (chartWidthAsPercent < 40) {
      closestMagnet.value = componentThirdWidth;
    } else {
      closestMagnet.value = componentHalfWidth;
    }
  }
};

// Функция завершения перетаскивания по оси X
const dragEndX = () => {
  chartDraggingX.value = false; // Задаем флаг перетаскивания по оси X false
  chartLoading.value = false; // Задаем флаг загрузки чарта false
  chartWidth.value = closestMagnet.value; // Примагничиваем ширину графика до ближайшего
  chartWrapperWidth.value = "100%"; // Задаем ширину обертки 100%
  dragZoneBorderRight.value = "none"; // Убираем подсветки "магнитов"
  // Скрываем чат, если размер чата занимает треть ширины и чат показывается
  if ((chartWidth.value === componentThirdWidth || chartWidth.value === componentHalfWidth) && showChat.value) {
    toggleShowChat();
  }
  // Если это пай чарт и мы довели его до трети, то "насильно" задаем ему максимальную высоту для корректного отображения
  if (currentChart.value?.type === "pie" && chartWidth.value === componentThirdWidth) {
    chartHeight.value = `${componentMaxHeight}px`;
    chartWrapperHeight.value = "100%";
  }
  // Удаляем слушатели
  document.removeEventListener("mousemove", draggingX);
  document.removeEventListener("mouseup", dragEndX);
  document.body.style.cursor = "default";
  document.body.style.userSelect = "auto";
};

// Функция начала растягивания по оси Y
const dragStartY = e => {
  chartDraggingY.value = true; // Задаем флаг ратсягивания по оси Y true
  chartLoading.value = true; // Задаем флаг загрузки графика true

  chartWrapperHeight.value = chartHeight.value; // Запоминаем текущую высоту чарта и задаем его обертке
  // Вешаем слушатель на mousemove и mouseup для document
  document.addEventListener("mousemove", draggingY);
  document.addEventListener("mouseup", dragEndY);
  // Назначаем нужный нам курсор
  document.body.style.cursor = "ns-resize";
  document.body.style.userSelect = "none";
};

// Функция растягивания по оси Y
const draggingY = e => {
  if (chartDraggingY.value) {
    // Получаем текущую позицию оси Y как если бы у нашего элемента не было отступов сверху
    const chartWrapperMarginTopFromWindow = chartWrapper.value?.getBoundingClientRect().top;
    const yWithoutMarginTop = e.y - chartWrapperMarginTopFromWindow;

    // Отклонение от максимальной величины из-за появления border
    const maxHeightBorderDeviation = 3;

    // Если элемент больше минимальной высоты или меньше минимальной высоты с учетом рамки
    // то назначаем ему высоту соответствующую позции курсора
    if (yWithoutMarginTop > componentMinHeight && yWithoutMarginTop < componentMaxHeight - maxHeightBorderDeviation) {
      chartWrapperHeight.value = `${yWithoutMarginTop}px`;
    }
  }
};

// Функция завершения растягивания по оси Y
const dragEndY = () => {
  chartDraggingY.value = false; // Задаем флаг растягивания по оси Y false
  chartLoading.value = false; // Задаем флаг загрузки графика false
  chartHeight.value = chartWrapperHeight.value; // Задаем высоту графика той что была при растягивании обертки
  chartWrapperHeight.value = "100%"; // Задаем высоту обертки 100%

  // Удаляем слушатели
  document.removeEventListener("mousemove", draggingY);
  document.removeEventListener("mouseup", dragEndY);
  // Назначаем обычный курсор
  document.body.style.cursor = "default";
  document.body.style.userSelect = "auto";
};

// До монтирования компонента, если есть id чата, запускаем функцию на получение этого чата
onBeforeMount(async () => {
  if (!id.value) {
    createNewChat();
  }
});
</script>

<style lang="scss" scoped>
.chart {
  &.draggingX {
    width: 100% !important;
    min-width: 100% !important;
    border: 2px dashed var(--ai-dashboard-chart-wrapper-border-color);
    border-radius: 32px;
  }
  &.draggingY {
    height: 653px !important;
    min-height: 653px !important;
    border: 2px dashed var(--ai-dashboard-chart-wrapper-border-color);
    border-radius: 32px;
  }
}

.chart-wrapper {
  height: 382px;
  background-color: var(--ai-dashboard-chat-background-color);
  border-radius: 32px;
  margin-top: 0;
  position: relative;
  display: flex;

  &.just-created {
    max-height: 382px;
    overflow-y: hidden;
    animation: openChart 0.4s ease-in-out;

    @keyframes openChart {
      0% {
        max-height: 80px;
      }
      100% {
        max-height: 382px;
      }
    }
  }

  .chart-component-wrapper {
    width: 100%;
    height: 100%;
    position: relative;
  }

  .chart-component {
    width: 100%;
    height: 100%;
  }

  .chat-visible-wrapper {
    width: 280px;
    min-width: 280px;
    position: relative;
    height: 100%;
    margin-right: 16px;
    z-index: 1;

    &.absolute {
      position: absolute;
    }

    .chart-chat-toggle-button {
      cursor: pointer;
      color: #000;
      position: absolute;
      right: 0;
      box-shadow: 0 0 30px -6px black;
      width: 26px;
      height: 26px;
      border-radius: 50%;
      font-size: 25px;
      padding: 0;
      right: -13px;
      top: 13px;
      border: none;
      background-color: #fff;

      i {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
      }
    }

    &.hidded {
      width: 0;
      min-width: 0;
      animation: hide-chat 0.2s ease-in-out;
      margin-right: 0;

      @keyframes hide-chat {
        0% {
          width: 280px;
          min-width: 280px;
          margin-right: 16px;
        }
        100% {
          border-radius: 14px;
          width: 30px;
          min-width: 30px;
          margin-right: 0;
        }
      }

      .chart-chat-toggle-button {
        right: initial;
        left: -13px;
        transform: rotate(180deg);

        animation: slide-to-left 0.2s ease-in-out;

        @keyframes slide-to-left {
          0% {
            right: -13px;
            left: initial;
            transform: rotate(0);
          }
          99% {
            right: -13px;
            left: initial;
          }
          100% {
            right: initial;
            left: -13px;
            transform: rotate(180deg);
          }
        }
      }
    }

    &.animate-open {
      animation: show-chat 0.2s;
      width: 280px;
      min-width: 280px;

      @keyframes show-chat {
        0% {
          width: 0;
          min-width: 0;
        }
        100% {
          width: 280px;
          min-width: 280px;
        }
      }

      .chart-chat-toggle-button {
        animation: slide-to-right 0.2s ease-in-out;

        @keyframes slide-to-right {
          0% {
            transform: rotate(180deg);
          }
          100% {
            transform: rotate(0);
          }
        }
      }
    }
  }

  .chat-wrapper {
    width: 100%;
    overflow-x: hidden;
    background-color: var(--ai-dashboard-chat-background-color);
    border-radius: 30px;
    box-shadow: 0px 4px 16px 0px #718eb51a;
    height: 100%;
  }

  .chart-chat {
    width: 280px;
    position: relative;
  }

  &-remove {
    position: absolute;
    top: 27px;
    right: 20px;
    width: 20px;
    cursor: pointer;
  }

  .chart-plug {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;
    overflow: hidden;
    color: var(--ai-dahsboard-chat-text-color);

    &-image {
      width: 300px;
    }

    b {
      font-size: 16px;
    }

    p {
      font-size: 14px;
    }
  }

  .chart-wrapper-drag-zone-X {
    position: absolute;
    right: -7px;
    width: 10px;
    height: 100%;
    transform: translateX(-50%);
    cursor: ew-resize;
  }

  .chart-wrapper-drag-zone-Y {
    position: absolute;
    bottom: -7px;
    width: 100%;
    height: 10px;
    transform: translateY(-50%);
    cursor: ns-resize;
  }

  .q-skeleton {
    cursor: inherit;
    border-radius: 32px;
  }
}
</style>

<style lang="scss">
.chart-wrapper svg.chart-wrapper-remove {
  path {
    stroke: var(--ai-dashboard-chart-icons-color);
  }
}
</style>
